import { AfterViewInit, Component, DestroyRef, EventEmitter, Input, Output, ViewChild, signal } from '@angular/core';
import {
  Agency,
  AGENCY_TYPE,
  AgencyKeys,
  AgencyMedia,
  Agent,
  AgentKeys,
  AGMedia,
  AGMediaKeys,
  Entity,
  EntityPermissionActivityKeys,
  EntityPermissionModelKeys,
  IAspectRatio,
  LookupKeys,
  Lookups,
  MediaSize,
} from '@ag-common-lib/public-api';
import { ModalWindowComponent } from '../../../modal-window/modal-window.component';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  firstValueFrom,
  lastValueFrom,
  map,
  mergeMap,
  Observable,
  shareReplay,
  take,
} from 'rxjs';
import { DxFormComponent } from 'devextreme-angular';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ToastrService } from 'ngx-toastr';
import { AGENCY_STATUS, AGENCY_TYPES_LOOKUP } from '@ag-common-lib/lib';
import { AgMediaUploaderModalComponent } from 'ag-common-svc/lib/components/ag-media-uploader/ag-media-uploader-modal.component';
import { DropZoneComponent } from 'ag-common-svc/lib/components/drop-zone/drop-zone.component';
import { ImageCropperModalComponent } from 'ag-common-svc/lib/components/image-cropper-modal/image-cropper-modal.component';
import {
  revokeCropperObjectUrl,
  waitForCropperImage,
} from 'ag-common-svc/lib/components/image-cropper-modal/image-cropper-modal.utils';
import { AgencyImagesService } from 'ag-common-svc/lib/services/agency-images.service';
import { AuthService, LookupsService } from 'ag-common-svc/public-api';
import { FullAddressPipe } from 'ag-common-svc/shared/pipes/full-address.pipe';
import { PhoneNumberMaskPipe } from 'ag-common-svc/shared/pipes/phone-number-mask.pipe';
import { WasabiImgPipe } from 'ag-common-svc/shared/pipes/wasabi-img.pipe';
import { AgencyHeaderKeys } from './agency-header.model';
import { AgencyHeaderService } from './agency-header.service';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { AgencyInfoModalComponent } from '../agency-info-model/agency-info-modal.component';

@UntilDestroy()
@Component({
  selector: 'ag-shr-agency-header',
  templateUrl: './agency-header.component.html',
  styleUrls: ['./agency-header.component.scss'],
  providers: [PhoneNumberMaskPipe, FullAddressPipe, WasabiImgPipe],
})
export class AgencyHeaderComponent implements AfterViewInit {
  private _agencyId$ = new BehaviorSubject(null);
  @Input()
  set agencyId(id: string) {
    this._agencyId = id;
    this.mediaPathPrefix = `agencies/${id}`;
    this._agencyId$.next(id);
    this.selectedAgencyLeads$ = this.agencyHeaderService.getSelectedAgencyLeads(id);
    this.agencyHeaderService.successfulCreatedForm();
    this.agencyMediaDataSource$ = this.agencyImagesService.fetchAgencyImages(id).pipe(shareReplay(1));
  }
  @Output() fieldsUpdated = new EventEmitter<{ agencyDbId: string; updates: Partial<Agency> }>();
  @ViewChild('agencyInfoModalRef', { static: true }) agencyInfoModalComponent: AgencyInfoModalComponent;
  @ViewChild('agencyHeaderFormRef', { static: false }) agencyHeaderFormComponent: DxFormComponent;
  @ViewChild('agenyCompanyLogoModalRef', { static: true }) agencyLogoModalComponent: ModalWindowComponent;
  @ViewChild('agencyLogoDropZoneRef', { static: true }) agencyLogoDropZoneComponent: DropZoneComponent;
  @ViewChild(ImageCropperModalComponent, { static: false }) imageCropperModalComponent: ImageCropperModalComponent; // ViewChild for DxTooltipComponent
  @ViewChild(AgMediaUploaderModalComponent, { static: false }) mediaUploaderComponent: AgMediaUploaderModalComponent; // ViewChild for DxTooltipComponent

  private _agencyId: string;

  agency$: Observable<Agency> = this._agencyId$.pipe(
    filter(Boolean),
    mergeMap(id => {
      return this.agencyHeaderService.getAgencyById(id);
    }),
  );
  headerLink$ = new BehaviorSubject<string>('');
  inProgress$ = this.agencyHeaderService.inProgress$;
  agencyLeads$ = combineLatest({ allAgencyLeads: this.agencyHeaderService.allAgencyLeads$, agency: this.agency$ }).pipe(
    map(({ agency, allAgencyLeads }) => {
      return allAgencyLeads.filter(principal => {
        const agencyId = principal?.[AgentKeys.p_agency_id];
        return agencyId && agencyId === agency?.[AgencyKeys.agencyId];
      });
    }),
  );
  mgas$ = this.agencyHeaderService.mgas$;
  rmds$ = this.agencyHeaderService.rmds$;
  selectedAgencyLeads$: Observable<Agent[]>;
  agencyStatusesLookup$ = this.lookupsService.agencyStatusesLookup$;
  agencyMediaDataSource$: Observable<AgencyMedia[]>;
  protected EntityPermissionActivityKeys = EntityPermissionActivityKeys;

  protected agencyHeaderFormDetailsSig = this.agencyHeaderService.formDataSig;
  aspectRatio: IAspectRatio = { w: 4, h: 3 };
  mediaPathPrefix: string;

  protected readonly AgencyKeys = AgencyKeys;
  protected readonly AgentKeys = AgentKeys;
  protected readonly LookupKeys = LookupKeys;
  protected readonly Lookups = Lookups;
  protected readonly agencyStatus = AGENCY_STATUS;
  protected readonly BaseModelKeys = BaseModelKeys;
  protected readonly agencyType = AGENCY_TYPE;
  protected readonly AGENCY_TYPES_LOOKUP = AGENCY_TYPES_LOOKUP;
  protected readonly AgencyHeaderKeys = AgencyHeaderKeys;
  protected readonly MediaSize = MediaSize;
  protected readonly EntityPermissionModelKeys = EntityPermissionModelKeys;
  protected readonly Entity = Entity;
  protected readonly isPreparingHeadshotCropperSig = signal(false);
  private headshotCropperObjectUrl: string | null = null;

  constructor(
    private agencyHeaderService: AgencyHeaderService,
    private phoneNumberMaskPipe: PhoneNumberMaskPipe,
    private fullAddressPipe: FullAddressPipe,
    private authService: AuthService,
    private lookupsService: LookupsService,
    private wasabiImgPipe: WasabiImgPipe,
    private agencyImagesService: AgencyImagesService,
    private toastrService: ToastrService,
    destroyRef: DestroyRef,
  ) {
    destroyRef.onDestroy(() => {
      revokeCropperObjectUrl(this.headshotCropperObjectUrl);
    });
  }

  ngAfterViewInit(): void {
    this.headerLink$.next(this.agencyHeaderFormDetailsSig()?.companyLogoLink ?? '');
  }

  saveAgencyLogoImagesUpdates = async (): Promise<void> => {
    const isImageValid = await lastValueFrom(this.agencyLogoDropZoneComponent?.isImageValid$.pipe(take(1)));
    if (isImageValid) {
      this.agencyHeaderService.handleSave(this._agencyId).then(data => {
        this.fieldsUpdated.emit(data);
        this.agencyLogoModalComponent.hideModal();
      });
    }
  };

  handleClosePopup = () => this.agencyHeaderService.onCancelEdit.call(this, this.onAfterRevertChanges);

  showEditorModal = async () => {
    const agency = await lastValueFrom(this.agency$.pipe(filter(Boolean), take(1)));

    this.agencyInfoModalComponent.showModal(agency);
  };

  onPictureUrlChange(link: string): void {
    setTimeout(() => {
      this.headerLink$.next(link);
      this.agencyHeaderFormDetailsSig().companyLogoLink = link;
      this.agencyHeaderService.updateFieldsData(this.agencyHeaderFormDetailsSig());
    }, 100);
  }

  phoneNumberDisplayExpr = item => this.phoneNumberMaskPipe.transform(item, true);

  fullAddressDisplayExpr = item => this.fullAddressPipe.transform(item);

  onAfterRevertChanges = (): void => {
    this.agencyHeaderFormComponent.instance.repaint();
  };

  onSelectionChanged = ({ selectedItem }): void => {
    this.agencyHeaderService.setSelectedAgencyStatus(selectedItem);
  };

  onUploadClicked = (): void => {
    this.mediaUploaderComponent.showModal();
  };

  handleCropImage = async (): Promise<void> => {
    const headshot = await (await firstValueFrom(this.agency$)).head_shot;
    this.imageCropperModalComponent.showModal(
      this.wasabiImgPipe.transform(headshot?.wasabiPath),
      headshot?.imageCropperPayload,
    );
  };

  onImageCropped = payload => this.agencyHeaderService.onImageCropped(this._agencyId, payload);

  onMediaChanged = async (mediaUrl: AGMedia): Promise<void> => {
    const wasabiPath = mediaUrl?.[AGMediaKeys.wasabiPath];
    if (!this._agencyId || !wasabiPath) {
      return;
    }

    this.isPreparingHeadshotCropperSig.set(true);

    try {
      await this.agencyHeaderService.onProfileImageChanged(this._agencyId, mediaUrl);

      const pictureUrl = this.wasabiImgPipe.transform(wasabiPath) ?? '';
      const cropperImageUrl = await waitForCropperImage(pictureUrl);
      if (!cropperImageUrl) {
        this.toastrService.error('Unable to load the uploaded image. Please try Edit Thumbnail again.');
        return;
      }

      revokeCropperObjectUrl(this.headshotCropperObjectUrl);
      this.headshotCropperObjectUrl = cropperImageUrl;
      this.imageCropperModalComponent.showModal(cropperImageUrl);
    } finally {
      this.isPreparingHeadshotCropperSig.set(false);
    }
  };
}
